import type { MaskedTile as Tile } from '@completto/shared';
import { rotationCandidates } from '@completto/shared';
import { html, View } from 'rune-ts';

export type LaneCellViewState =
  | 'DOWN'
  | 'UP'
  | 'PLACEABLE'
  | 'IN_ZONE'
  | 'SELECTED'
  | 'JUST_PLACED'
  | 'DOWN_DRAGGABLE' // 비공개 + 내 차례 + holding 없음 → 드래그 가능
  | 'INSERT_SOURCE'; // 끼워넣기 드래그 중인 출발 자리 (점선 placeholder)

export type LaneCellData = {
  tile: Tile;
  idx: number;
  state: LaneCellViewState;
  onClick?: (idx: number) => void;
  onPointerDown?: (
    idx: number,
    pointerId: number,
    clientX: number,
    clientY: number,
    el: HTMLElement,
  ) => void;
};

export class LaneCellView extends View<LaneCellData> {
  override template() {
    const { tile, idx, state } = this.data;
    const isUp = tile.face === 'UP' && tile.resolvedNumber !== null;
    const showHidden =
      !isUp && (state === 'DOWN' || state === 'DOWN_DRAGGABLE' || state === 'IN_ZONE');
    const text = isUp ? String(tile.resolvedNumber) : showHidden ? '?' : '';
    const rotatable =
      tile.face === 'UP' && tile.resolvedNumber !== null && tile.number !== null
        ? rotationCandidates(tile.number).length > 1
        : false;
    const interactive = state === 'PLACEABLE' || state === 'SELECTED' || state === 'DOWN_DRAGGABLE';
    const draggable = state === 'DOWN_DRAGGABLE' || state === 'INSERT_SOURCE';
    const cls = this.classes(state, tile.face === 'UP');
    const touchAction = draggable ? 'touch-action:none;' : '';
    const sparkle =
      state === 'JUST_PLACED'
        ? html`<span class="lane-cell-sparkle absolute left-1/2 -top-2 text-base leading-none pointer-events-none select-none">✨</span>`
        : html``;
    const confirmHint =
      state === 'SELECTED'
        ? html`<span class="lane-cell-confirm-hint absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-card-gold text-wood-ink text-[10px] font-extrabold leading-none shadow-sm pointer-events-none select-none animate-pulse" aria-hidden="true">✓</span>`
        : html``;
    const ariaLabel = buildAriaLabel(idx, tile, state);
    return html`
      <button
        type="button"
        data-lane-cell-idx="${idx}"
        aria-label="${ariaLabel}"
        style="${touchAction}"
        class="${cls} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        ${interactive ? '' : 'disabled'}
      >
        <span class="lane-cell-num leading-none">${text}</span>
        ${
          rotatable
            ? html`<span class="lane-cell-rot absolute bottom-0.5 right-0.5 text-[16px] opacity-50 leading-none">⟲</span>`
            : html``
        }
        ${sparkle}
        ${confirmHint}
      </button>
    `;
  }

  protected override onMount(): void {
    const el = this.element();
    el.addEventListener('click', () => {
      const onClick = this.data.onClick;
      if (!onClick) return;
      const { idx, state } = this.data;
      if (state === 'PLACEABLE' || state === 'SELECTED' || state === 'DOWN_DRAGGABLE') {
        onClick(idx);
      }
    });

    el.addEventListener('pointerdown', (e: PointerEvent) => {
      const onPointerDown = this.data.onPointerDown;
      if (!onPointerDown) return;
      const { idx, state } = this.data;
      if (state !== 'DOWN_DRAGGABLE') return;
      // 좌클릭/터치만
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      e.preventDefault();
      onPointerDown(idx, e.pointerId, e.clientX, e.clientY, el);
    });
  }

  // (helpers below)

  private classes(state: LaneCellViewState, isUp: boolean): string {
    const base =
      'lane-cell relative w-9 h-12 sm:w-11 sm:h-14 mlandscape:w-7 mlandscape:h-9 flex items-center justify-center rounded-md select-none transition-all font-bold text-base mlandscape:text-sm shrink-0';
    switch (state) {
      case 'DOWN':
        return `${base} lane-cell-down bg-gradient-to-br from-wood-bark to-wood-coffee text-wood-paper/35 cursor-default`;
      case 'UP':
        return `${base} lane-cell-up bg-card-navy text-card-gold cursor-default`;
      case 'IN_ZONE':
        return `${base} lane-cell-in-zone bg-gradient-to-br from-wood-bark/40 to-wood-coffee/40 text-wood-paper/15 cursor-default`;
      case 'PLACEABLE':
        return `${base} lane-cell-placeable bg-card-gold/20 text-wood-ink ring-2 ring-inset ring-card-gold animate-pulse cursor-pointer hover:bg-card-gold/30`;
      case 'SELECTED':
        return `${base} lane-cell-selected bg-card-gold/40 text-wood-ink ring-4 ring-inset ring-card-brass cursor-pointer`;
      case 'JUST_PLACED':
        return `${base} lane-cell-just-placed bg-card-navy text-card-gold ring-4 ring-inset ring-card-gold`;
      case 'DOWN_DRAGGABLE':
        return `${base} lane-cell-down-draggable bg-gradient-to-br from-wood-bark to-wood-coffee text-wood-paper/40 ring-1 ring-inset ring-card-gold/40 cursor-grab hover:from-wood-coffee hover:to-wood-ink hover:ring-card-gold/70 active:cursor-grabbing`;
      case 'INSERT_SOURCE':
        return `${base} lane-cell-insert-source bg-transparent text-transparent`;
      default:
        if (isUp) return `${base} bg-card-navy text-card-gold`;
        return `${base} bg-wood-bark text-wood-paper/15`;
    }
  }
}

function buildAriaLabel(idx: number, tile: Tile, state: LaneCellViewState): string {
  const pos = `${idx + 1}번 자리`;
  if (tile.face === 'UP' && tile.resolvedNumber !== null) {
    return `${pos}, 공개 ${tile.resolvedNumber}`;
  }
  if (state === 'PLACEABLE') return `${pos}, 비공개, 놓기 가능`;
  if (state === 'SELECTED') return `${pos}, 비공개, 선택됨, 다시 탭하면 확정`;
  if (state === 'DOWN_DRAGGABLE') return `${pos}, 비공개, 드래그 가능`;
  if (state === 'INSERT_SOURCE') return `${pos}, 비공개, 끼워넣기 시작 자리`;
  if (state === 'IN_ZONE') return `${pos}, 비공개, 같은 그룹`;
  return `${pos}, 비공개`;
}
